import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from '../locales/en.json';
import ru from '../locales/ru.json';

export const SUPPORTED_LOCALES = ['en', 'ru'] as const;
export type AppLocale = (typeof SUPPORTED_LOCALES)[number];

export const LOCALE_STORAGE_KEY = 'license-dashboard.locale';

function isAppLocale(value: unknown): value is AppLocale {
  return value === 'en' || value === 'ru';
}

export function getSavedLocale(): AppLocale | null {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    return isAppLocale(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Locale resolution order:
 *   1. user's saved choice in localStorage
 *   2. server-provided DEFAULT_LOCALE (via GET /api/config)
 *   3. 'en'
 */
export function resolveLocale(saved: string | null, serverDefault: unknown): AppLocale {
  if (isAppLocale(saved)) return saved;
  if (isAppLocale(serverDefault)) return serverDefault;
  return 'en';
}

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    ru: { translation: ru },
  },
  lng: getSavedLocale() ?? 'en',
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
  returnEmptyString: false,
});

function syncHtmlLang(lng: string): void {
  document.documentElement.lang = lng.startsWith('ru') ? 'ru' : 'en';
}

syncHtmlLang(i18n.language);
i18n.on('languageChanged', syncHtmlLang);

/** Persist + switch the active locale (used by the language switcher). */
export async function setLocale(locale: AppLocale): Promise<void> {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // storage unavailable — still switch for this session
  }
  await i18n.changeLanguage(locale);
}

/**
 * Boot-time locale resolution. A saved user choice always wins;
 * otherwise ask the server for its deployment-configured default.
 * Failures silently keep the 'en' fallback.
 */
export async function initLocale(): Promise<void> {
  const saved = getSavedLocale();
  if (saved) {
    await i18n.changeLanguage(saved);
    return;
  }
  try {
    const resp = await fetch('/api/config');
    if (!resp.ok) return;
    const json: unknown = await resp.json();
    const serverDefault = (json as { defaultLocale?: unknown }).defaultLocale;
    await i18n.changeLanguage(resolveLocale(null, serverDefault));
  } catch {
    // server unreachable — keep fallback
  }
}

export default i18n;
